import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { LeaveRecord, ManageLeave, ManageLeaveRequest } from './LeaveAction';

interface LeavePopupProps {
  isOpen: boolean;
  onClose: () => void;
  leaveData?: LeaveRecord | null;
  mode: 'apply' | 'edit' | 'view';
  employeeCode?: string;
  employeeName?: string;
  onSubmitSuccess?: () => void;
  ManageLeave: (leaveData: ManageLeaveRequest) => Promise<any>;
}

const popupStyles = `
  .lm-popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    animation: fadeIn 0.3s ease;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes slideUp {
    from { transform: translateY(30px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }

  .lm-popup-container {
    width: 90%;
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
    background: linear-gradient(160deg, #0b1f2e 0%, #0a1a28 100%);
    border: 1px solid rgba(0, 210, 200, 0.25);
    border-radius: 20px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.6), 0 0 30px rgba(0, 160, 155, 0.2);
    animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    position: relative;
  }

  .lm-popup-header {
    padding: 20px 24px;
    border-bottom: 1px solid rgba(0, 210, 200, 0.15);
    background: linear-gradient(90deg, rgba(0, 160, 155, 0.2) 0%, rgba(0, 120, 175, 0.1) 100%);
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: sticky;
    top: 0;
    backdrop-filter: blur(12px);
    z-index: 10;
    border-radius: 20px 20px 0 0;
  }

  .lm-popup-title {
    font-size: 20px;
    font-weight: 700;
    color: #e8f4f6;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .lm-popup-title-icon {
    width: 36px;
    height: 36px;
    background: rgba(0, 210, 200, 0.15);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    border: 1px solid rgba(0, 210, 200, 0.3);
  }

  .lm-popup-close {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(0, 210, 200, 0.2);
    color: #7ab0b8;
    font-size: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
  }

  .lm-popup-close:hover {
    background: rgba(255, 70, 70, 0.15);
    border-color: rgba(255, 70, 70, 0.4);
    color: #ff6b6b;
    transform: rotate(90deg);
  }

  .lm-popup-body {
    padding: 24px;
  }

  .lm-popup-section {
    margin-bottom: 24px;
  }

  .lm-popup-section-title {
    font-size: 14px;
    font-weight: 600;
    color: #7ab0b8;
    margin-bottom: 16px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-left: 3px solid #00d2c8;
    padding-left: 12px;
  }

  .lm-popup-row {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    margin-bottom: 16px;
  }

  .lm-popup-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .lm-popup-field.full-width {
    grid-column: span 2;
  }

  .lm-popup-label {
    font-size: 12px;
    font-weight: 600;
    color: #a0d0d8;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .lm-popup-required {
    color: #ff6b6b;
    font-size: 14px;
  }

  .lm-popup-input {
    padding: 12px 14px;
    background: rgba(10, 25, 40, 0.8);
    border: 1px solid rgba(0, 210, 200, 0.2);
    border-radius: 12px;
    font-size: 14px;
    color: #e8f4f6;
    font-family: 'DM Sans', sans-serif;
    transition: all 0.2s;
  }

  .lm-popup-input:focus {
    outline: none;
    border-color: #00d2c8;
    box-shadow: 0 0 0 3px rgba(0, 210, 200, 0.1);
    background: rgba(15, 35, 55, 0.9);
  }

  .lm-popup-input:hover:not(:focus) {
    border-color: rgba(0, 210, 200, 0.4);
  }

  .lm-popup-input.error {
    border-color: #ff6b6b;
    background: rgba(255, 70, 70, 0.05);
  }

  .lm-popup-select {
    padding: 12px 14px;
    background: rgba(10, 25, 40, 0.8);
    border: 1px solid rgba(0, 210, 200, 0.2);
    border-radius: 12px;
    font-size: 14px;
    color: #e8f4f6;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer;
  }

  .lm-popup-select option {
    background: #0b1f2e;
    color: #e8f4f6;
  }

  .lm-popup-textarea {
    padding: 12px 14px;
    background: rgba(10, 25, 40, 0.8);
    border: 1px solid rgba(0, 210, 200, 0.2);
    border-radius: 12px;
    font-size: 14px;
    color: #e8f4f6;
    font-family: 'DM Sans', sans-serif;
    min-height: 100px;
    resize: vertical;
  }

  .lm-popup-info-box {
    background: linear-gradient(135deg, rgba(0, 160, 155, 0.1) 0%, rgba(0, 120, 175, 0.05) 100%);
    border: 1px solid rgba(0, 210, 200, 0.15);
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .lm-popup-info-icon {
    width: 40px;
    height: 40px;
    background: rgba(0, 210, 200, 0.15);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
  }

  .lm-popup-info-content {
    flex: 1;
  }

  .lm-popup-info-title {
    font-size: 13px;
    font-weight: 600;
    color: #7ab0b8;
    margin-bottom: 4px;
  }

  .lm-popup-info-text {
    font-size: 15px;
    font-weight: 700;
    color: #e8f4f6;
  }

  .lm-popup-balance-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin-top: 10px;
  }

  .lm-popup-balance-card {
    background: rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(0, 210, 200, 0.1);
    border-radius: 10px;
    padding: 12px;
    text-align: center;
  }

  .lm-popup-balance-label {
    font-size: 11px;
    color: #7ab0b8;
    margin-bottom: 4px;
  }

  .lm-popup-balance-value {
    font-size: 18px;
    font-weight: 700;
    color: #00d2c8;
  }

  .lm-popup-balance-remaining {
    font-size: 11px;
    color: #4a9e9e;
  }

  .lm-popup-footer {
    padding: 20px 24px;
    border-top: 1px solid rgba(0, 210, 200, 0.15);
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 0 0 20px 20px;
  }

  .lm-popup-btn {
    padding: 12px 24px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
    font-family: 'DM Sans', sans-serif;
  }

  .lm-popup-btn-primary {
    background: linear-gradient(135deg, #00d2c8 0%, #00a0b8 100%);
    color: #0b1f2e;
    box-shadow: 0 4px 15px rgba(0, 210, 200, 0.3);
  }

  .lm-popup-btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 210, 200, 0.4);
  }

  .lm-popup-btn-secondary {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(0, 210, 200, 0.2);
    color: #7ab0b8;
  }

  .lm-popup-btn-secondary:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(0, 210, 200, 0.4);
  }

  .lm-popup-btn-danger {
    background: rgba(255, 70, 70, 0.1);
    border: 1px solid rgba(255, 70, 70, 0.3);
    color: #ff6b6b;
  }

  .lm-popup-btn-danger:hover {
    background: rgba(255, 70, 70, 0.2);
    border-color: rgba(255, 70, 70, 0.5);
  }

  .lm-popup-error {
    background: rgba(255, 70, 70, 0.1);
    border: 1px solid rgba(255, 70, 70, 0.3);
    border-radius: 10px;
    padding: 12px;
    margin-bottom: 16px;
    color: #ff6b6b;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .lm-popup-success {
    background: rgba(0, 210, 200, 0.1);
    border: 1px solid rgba(0, 210, 200, 0.3);
    border-radius: 10px;
    padding: 12px;
    margin-bottom: 16px;
    color: #00d2c8;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .lm-popup-loading {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid rgba(0, 210, 200, 0.3);
    border-radius: 50%;
    border-top-color: #00d2c8;
    animation: spin 1s ease-in-out infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* Date picker customization */
  .lm-popup-input[type="date"]::-webkit-calendar-picker-indicator {
    filter: invert(0.8);
    cursor: pointer;
  }

  /* Responsive */
  @media (max-width: 600px) {
    .lm-popup-row {
      grid-template-columns: 1fr;
    }
    
    .lm-popup-field.full-width {
      grid-column: span 1;
    }
    
    .lm-popup-balance-grid {
      grid-template-columns: 1fr;
    }
  }
`;

// Mapping function to convert between different leave type formats
const mapLeaveType = (type: string | undefined): string => {
  if (!type) return '';
  
  // Handle different formats that might come from the backend
  const typeMap: { [key: string]: string } = {
    'Annual': 'Annual',
    'Annual Leave': 'Annual',
    'Sick': 'Sick',
    'Sick Leave': 'Sick',
    'Casual': 'Casual',
    'Casual Leave': 'Casual',
    'Maternity': 'Maternity',
    'Maternity Leave': 'Maternity',
    'Paternity': 'Paternity',
    'Paternity Leave': 'Paternity',
    'Bereavement': 'Bereavement',
    'Bereavement Leave': 'Bereavement'
  };
  
  return typeMap[type] || type;
};

const LeavePopup: React.FC<LeavePopupProps> = ({
  isOpen,
  onClose,
  leaveData,
  mode,
  employeeCode = '',
  employeeName = '',
  onSubmitSuccess,
  ManageLeave,
}) => {
  const [formData, setFormData] = useState({
    leaveType: '',
    fromDate: '',
    toDate: '',
    reason: '',
    halfDay: false,
    contactNumber: '',
    alternateContact: '',
    emergencyContact: '',
    documents: null as File | null
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [calculatedDays, setCalculatedDays] = useState(1);

  const isSubmitting = loading;
  const isViewMode = mode === 'view';

  // Populate form data when editing or viewing
  useEffect(() => {
    if ((mode === 'edit' || mode === 'view') && leaveData) {
      console.log('Populating edit form with leaveData:', leaveData);
      
      setFormData({
        leaveType: mapLeaveType(leaveData.leaveType) || '',
        fromDate: leaveData.fromDate ? new Date(leaveData.fromDate).toISOString().split('T')[0] : '',
        toDate: leaveData.toDate ? new Date(leaveData.toDate).toISOString().split('T')[0] : '',
        reason: leaveData.reason || '',
        halfDay: false,
        contactNumber: '',
        alternateContact: '',
        emergencyContact: '',
        documents: null
      });
    } else {
      // Reset form for apply mode
      setFormData({
        leaveType: '',
        fromDate: '',
        toDate: '',
        reason: '',
        halfDay: false,
        contactNumber: '',
        alternateContact: '',
        emergencyContact: '',
        documents: null
      });
    }
    setErrors({});
    setSubmitSuccess(false);
  }, [mode, leaveData, isOpen]);

  // Calculate number of days between dates
  useEffect(() => {
    if (formData.fromDate && formData.toDate) {
      const from = new Date(formData.fromDate);
      const to = new Date(formData.toDate);
      const diffTime = Math.abs(to.getTime() - from.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      setCalculatedDays(diffDays);
    } else {
      setCalculatedDays(1);
    }
  }, [formData.fromDate, formData.toDate]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.leaveType) {
      newErrors.leaveType = 'Leave type is required';
    }

    if (!formData.fromDate) {
      newErrors.fromDate = 'From date is required';
    }

    if (!formData.toDate) {
      newErrors.toDate = 'To date is required';
    }

    if (formData.fromDate && formData.toDate) {
      const from = new Date(formData.fromDate);
      const to = new Date(formData.toDate);
      
      if (to < from) {
        newErrors.toDate = 'To date cannot be before from date';
      }

      // Check if selected dates are in the past (only for new applications)
      if (mode === 'apply') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (from < today) {
          newErrors.fromDate = 'Cannot apply for past dates';
        }
      }
    }

    if (!formData.reason) {
      newErrors.reason = 'Reason is required';
    } else if (formData.reason.length < 10) {
      newErrors.reason = 'Please provide a detailed reason (minimum 10 characters)';
    }

    // Only validate contact numbers for apply/edit mode
    if (mode !== 'view') {
      if (!formData.contactNumber) {
        newErrors.contactNumber = 'Contact number is required';
      } else if (!/^\d{10}$/.test(formData.contactNumber.replace(/\D/g, ''))) {
        newErrors.contactNumber = 'Please enter a valid 10-digit number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    if (isViewMode) return;
    
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isViewMode) return;
    
    if (e.target.files && e.target.files[0]) {
      setFormData(prev => ({
        ...prev,
        documents: e.target.files![0]
      }));
    }
  };

  const handleSubmit = async () => {
    if (isViewMode) return;
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSubmitSuccess(false);

    try {
      // Convert the leave type back to full format if needed
      const leaveTypeMap: { [key: string]: string } = {
        'Annual': 'Annual Leave',
        'Sick': 'Sick Leave',
        'Casual': 'Casual Leave',
        'Maternity': 'Maternity Leave',
        'Paternity': 'Paternity Leave',
        'Bereavement': 'Bereavement Leave'
      };

      // Prepare the leave data for API with exact request body structure from example
      const leaveRequest: ManageLeaveRequest = {
        appliedOn: new Date().toISOString().split('T')[0], // Current date in YYYY-MM-DD format
        days: calculatedDays,
        employeeCode: employeeCode,
        fromDate: formData.fromDate,
        leaveRequestId: "", // Empty string for new requests as per your example
        leaveType: leaveTypeMap[formData.leaveType] || formData.leaveType, // Convert to full format
        operation: mode === 'edit' ? "U" : "I", // I for Insert, U for Update
        status: mode === 'edit' ? (leaveData?.status || "Pending") : "Pending",
        toDate: formData.toDate,
        updatedBy: "system@example.com", // This should come from your auth system
      };

      // For edit mode, we need to set the leaveRequestId from the existing record
      if (mode === 'edit' && leaveData?.leaveRequestId) {
        leaveRequest.leaveRequestId = leaveData.leaveRequestId;
      }

      console.log('Submitting leave request with operation:', leaveRequest.operation);
      console.log('Request payload:', leaveRequest);

      const result = await ManageLeave(leaveRequest);

      if (result.success) {
        setSubmitSuccess(true);
        
        // Close popup after success
        setTimeout(() => {
          onClose();
          if (onSubmitSuccess) {
            onSubmitSuccess();
          }
        }, 1500);
      } else {
        setErrors({ submit: result.message || 'Failed to submit leave request' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({ submit: 'Failed to submit leave request. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!leaveData || isViewMode) return;
    
    if (!window.confirm('Are you sure you want to delete this leave request?')) {
      return;
    }

    setLoading(true);

    try {
      // D = Delete operation with exact request body structure from example
      const deleteRequest: ManageLeaveRequest = {
        appliedOn: new Date().toISOString().split('T')[0],
        days: leaveData.days || calculatedDays,
        employeeCode: employeeCode,
        fromDate: leaveData.fromDate,
        leaveRequestId: leaveData.leaveRequestId || "",
        leaveType: leaveData.leaveType,
        operation: "D", // D for Delete
        status: leaveData.status || "Pending",
        toDate: leaveData.toDate,
        updatedBy: "system@example.com", // This should come from your auth system
      };

      console.log('Deleting leave request with operation: D');
      console.log('Delete payload:', deleteRequest);

      const result = await ManageLeave(deleteRequest);

      if (result.success) {
        alert('Leave request deleted successfully!');
        onClose();
        if (onSubmitSuccess) {
          onSubmitSuccess();
        }
      } else {
        alert(result.message || 'Failed to delete leave request');
      }
    } catch (error) {
      console.error('Error deleting leave request:', error);
      alert('Failed to delete leave request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getOperationBadge = () => {
    if (mode === 'apply') return { text: 'INSERT', color: '#00d2c8' };
    if (mode === 'edit') return { text: 'UPDATE', color: '#ffaa00' };
    return { text: 'VIEW', color: '#7ab0b8' };
  };

  if (!isOpen) return null;

  const operationBadge = getOperationBadge();

  return (
    <>
      <style>{popupStyles}</style>
      <div className="lm-popup-overlay" onClick={onClose}>
        <div className="lm-popup-container" onClick={e => e.stopPropagation()}>
          <div className="lm-popup-header">
            <div className="lm-popup-title">
              <span className="lm-popup-title-icon">
                {mode === 'apply' ? '📝' : mode === 'edit' ? '✏️' : '👁️'}
              </span>
              {mode === 'apply' ? 'Apply for Leave' : mode === 'edit' ? 'Edit Leave Request' : 'View Leave Request'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{
                background: 'rgba(0, 210, 200, 0.1)',
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '11px',
                fontWeight: '600',
                color: operationBadge.color,
                border: `1px solid ${operationBadge.color}30`
              }}>
                {operationBadge.text}
              </span>
              <div className="lm-popup-close" onClick={onClose}>×</div>
            </div>
          </div>

          <div className="lm-popup-body">
            {/* Employee Info */}
            <div className="lm-popup-info-box">
              <div className="lm-popup-info-icon">👤</div>
              <div className="lm-popup-info-content">
                <div className="lm-popup-info-title">Employee</div>
                <div className="lm-popup-info-text">
                  {employeeName || 'Loading...'} ({employeeCode || 'N/A'})
                </div>
              </div>
              {leaveData?.status && mode === 'view' && (
                <div className={`lm-status-badge ${leaveData.status.toLowerCase()}`}>
                  {leaveData.status}
                </div>
              )}
            </div>

            {/* Error/Success Messages */}
            {errors.submit && (
              <div className="lm-popup-error">
                <span>⚠️</span>
                {errors.submit}
              </div>
            )}

            {submitSuccess && (
              <div className="lm-popup-success">
                <span>✅</span>
                Leave request {mode === 'apply' ? 'submitted' : 'updated'} successfully!
              </div>
            )}

            {/* Form */}
            <div className="lm-popup-section">
              <div className="lm-popup-section-title">Leave Details</div>
              
              <div className="lm-popup-row">
                <div className="lm-popup-field">
                  <label className="lm-popup-label">
                    Leave Type {!isViewMode && <span className="lm-popup-required">*</span>}
                  </label>
                  <select
                    name="leaveType"
                    value={formData.leaveType}
                    onChange={handleInputChange}
                    className={`lm-popup-select ${errors.leaveType ? 'error' : ''}`}
                    disabled={isViewMode || isSubmitting}
                  >
                    <option value="">Select Leave Type</option>
                    <option value="Annual">Annual Leave</option>
                    <option value="Sick">Sick Leave</option>
                    <option value="Casual">Casual Leave</option>
                    <option value="Maternity">Maternity Leave</option>
                    <option value="Paternity">Paternity Leave</option>
                    <option value="Bereavement">Bereavement Leave</option>
                  </select>
                  {errors.leaveType && (
                    <div style={{ color: '#ff6b6b', fontSize: '11px', marginTop: '4px' }}>
                      {errors.leaveType}
                    </div>
                  )}
                </div>

                {!isViewMode && (
                  <div className="lm-popup-field">
                    <label className="lm-popup-label">Half Day</label>
                    <div style={{ display: 'flex', alignItems: 'center', height: '46px' }}>
                      <input
                        type="checkbox"
                        name="halfDay"
                        checked={formData.halfDay}
                        onChange={handleInputChange}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        disabled={isSubmitting}
                      />
                      <span style={{ marginLeft: '8px', color: '#7ab0b8', fontSize: '13px' }}>
                        Apply for half day
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="lm-popup-row">
                <div className="lm-popup-field">
                  <label className="lm-popup-label">
                    From Date {!isViewMode && <span className="lm-popup-required">*</span>}
                  </label>
                  <input
                    type="date"
                    name="fromDate"
                    value={formData.fromDate}
                    onChange={handleInputChange}
                    className={`lm-popup-input ${errors.fromDate ? 'error' : ''}`}
                    min={mode === 'apply' ? new Date().toISOString().split('T')[0] : undefined}
                    disabled={isViewMode || isSubmitting}
                  />
                  {errors.fromDate && (
                    <div style={{ color: '#ff6b6b', fontSize: '11px', marginTop: '4px' }}>
                      {errors.fromDate}
                    </div>
                  )}
                </div>

                <div className="lm-popup-field">
                  <label className="lm-popup-label">
                    To Date {!isViewMode && <span className="lm-popup-required">*</span>}
                  </label>
                  <input
                    type="date"
                    name="toDate"
                    value={formData.toDate}
                    onChange={handleInputChange}
                    className={`lm-popup-input ${errors.toDate ? 'error' : ''}`}
                    min={formData.fromDate || (mode === 'apply' ? new Date().toISOString().split('T')[0] : undefined)}
                    disabled={isViewMode || isSubmitting}
                  />
                  {errors.toDate && (
                    <div style={{ color: '#ff6b6b', fontSize: '11px', marginTop: '4px' }}>
                      {errors.toDate}
                    </div>
                  )}
                </div>
              </div>

              {formData.fromDate && formData.toDate && (
                <div style={{ 
                  background: 'rgba(0, 210, 200, 0.05)', 
                  padding: '10px', 
                  borderRadius: '8px',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>
                  <span style={{ color: '#7ab0b8' }}>Total Days: </span>
                  <span style={{ color: '#00d2c8', fontWeight: 'bold', fontSize: '16px' }}>
                    {calculatedDays} {calculatedDays === 1 ? 'day' : 'days'}
                  </span>
                </div>
              )}

              <div className="lm-popup-field full-width">
                <label className="lm-popup-label">
                  Reason for Leave {!isViewMode && <span className="lm-popup-required">*</span>}
                </label>
                <textarea
                  name="reason"
                  value={formData.reason}
                  onChange={handleInputChange}
                  className={`lm-popup-textarea ${errors.reason ? 'error' : ''}`}
                  placeholder="Please provide detailed reason for your leave request..."
                  disabled={isViewMode || isSubmitting}
                />
                {errors.reason && (
                  <div style={{ color: '#ff6b6b', fontSize: '11px', marginTop: '4px' }}>
                    {errors.reason}
                  </div>
                )}
                <div style={{ 
                  fontSize: '11px', 
                  color: '#7ab0b8', 
                  textAlign: 'right',
                  marginTop: '4px'
                }}>
                  {formData.reason.length}/500 characters
                </div>
              </div>
            </div>

            {/* Contact Information */}
            {mode !== 'view' && (
              <div className="lm-popup-section">
                <div className="lm-popup-section-title">Contact Information</div>
                
                <div className="lm-popup-row">
                  <div className="lm-popup-field">
                    <label className="lm-popup-label">
                      Contact Number <span className="lm-popup-required">*</span>
                    </label>
                    <input
                      type="tel"
                      name="contactNumber"
                      value={formData.contactNumber}
                      onChange={handleInputChange}
                      className={`lm-popup-input ${errors.contactNumber ? 'error' : ''}`}
                      placeholder="10-digit mobile number"
                      maxLength={10}
                      disabled={isSubmitting}
                    />
                    {errors.contactNumber && (
                      <div style={{ color: '#ff6b6b', fontSize: '11px', marginTop: '4px' }}>
                        {errors.contactNumber}
                      </div>
                    )}
                  </div>

                  <div className="lm-popup-field">
                    <label className="lm-popup-label">Alternate Contact</label>
                    <input
                      type="tel"
                      name="alternateContact"
                      value={formData.alternateContact}
                      onChange={handleInputChange}
                      className="lm-popup-input"
                      placeholder="Optional"
                      maxLength={10}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Documents - Only for apply/edit */}
            {mode !== 'view' && (
              <div className="lm-popup-section">
                <div className="lm-popup-section-title">Supporting Documents</div>
                
                <div className="lm-popup-field full-width">
                  <label className="lm-popup-label">Upload Documents (if any)</label>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    className="lm-popup-input"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    multiple
                    disabled={isSubmitting}
                  />
                  <div style={{ fontSize: '11px', color: '#7ab0b8', marginTop: '4px' }}>
                    Accepted formats: PDF, DOC, DOCX, JPG, PNG (Max size: 5MB)
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="lm-popup-footer">
            <button 
              className="lm-popup-btn lm-popup-btn-secondary" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            
            {mode === 'edit' && (
              <button 
                className="lm-popup-btn lm-popup-btn-danger" 
                onClick={handleDelete}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Deleting...' : 'Delete'}
              </button>
            )}
            
            {mode !== 'view' && (
              <button 
                className="lm-popup-btn lm-popup-btn-primary" 
                onClick={handleSubmit}
                disabled={isSubmitting}
                style={{ opacity: isSubmitting ? 0.7 : 1 }}
              >
                {isSubmitting ? (
                  <>
                    <span className="lm-popup-loading" style={{ marginRight: '8px' }}></span>
                    {mode === 'apply' ? 'Inserting...' : 'Updating...'}
                  </>
                ) : (
                  mode === 'apply' ? 'Submit' : 'Update'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// Map dispatch to props
const mapDispatchToProps = {
  ManageLeave
};

export default connect(null, mapDispatchToProps)(LeavePopup);